/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { dagRepository } from '@/services/repositories/dag.repository.js';

/**
 * DAG Store
 * Manages DAG check state and provides actions to interact with DAG data
 */
export const useDagStore = defineStore('dag', () => {
  // State
  const dagChecks = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Computed/Getters
  const checkCount = computed(() => dagChecks.value.length);

  /**
   * Get checks with critical scores (4 or 5 indicating severe condition)
   */
  const criticalChecks = computed(() => 
    dagChecks.value.filter(check => check.score >= 4)
  );

  /**
   * Get checks with concerning scores (3 or higher)
   */
  const concerningChecks = computed(() => 
    dagChecks.value.filter(check => check.score >= 3)
  );

  /**
   * Get healthy checks (score 1-2)
   */
  const healthyChecks = computed(() => 
    dagChecks.value.filter(check => check.score <= 2)
  );

  /**
   * Get the latest check (most recent)
   */
  const latestCheck = computed(() => 
    dagChecks.value.length > 0 ? dagChecks.value[0] : null
  );

  // Actions
  /**
   * Fetch all DAG checks for a specific goat
   * @param {number} goatId - Goat ID
   * @returns {Promise<void>}
   */
  async function fetchDagChecks(goatId) {
    loading.value = true;
    error.value = null;
    
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error('Invalid Goat ID.');
      
      dagChecks.value = await dagRepository.findByGoat(id);
      console.log(`Fetched ${dagChecks.value.length} DAG checks for goat ${id}`);
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch DAG checks:', e);
      dagChecks.value = [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch all DAG checks (for analytics/reports)
   * @returns {Promise<void>}
   */
  async function fetchAllDagChecks() {
    loading.value = true;
    error.value = null;
    
    try {
      dagChecks.value = await dagRepository.findAll();
      console.log(`Fetched ${dagChecks.value.length} total DAG checks`);
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch all DAG checks:', e);
      dagChecks.value = [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get a single DAG check by ID
   * @param {number} id - Check ID
   * @returns {Promise<import('../models/dag.model.js').DagCheck|null>}
   */
  async function getDagCheckById(id) {
    loading.value = true;
    error.value = null;
    
    try {
      const check = await dagRepository.findById(id);
      console.log(`Fetched DAG check ${id}`);
      return check;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch DAG check:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create a new DAG check
   * @param {{goatId:number, score:number, measuredAt?:string}} payload
   * @returns {Promise<number|null>}
   */
  async function createDagCheck(payload) {
    loading.value = true;
    error.value = null;
    
    try {
      const id = await dagRepository.create(payload);
      console.log(`Created DAG check ${id}`);
      
      // Refresh the list if we're viewing this goat's checks
      if (dagChecks.value.length > 0 && dagChecks.value[0].goatId === payload.goatId) {
        await fetchDagChecks(payload.goatId);
      }
      
      return id;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to create DAG check:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update an existing DAG check
   * @param {number} id
   * @param {{score:number, measuredAt?:string}} payload
   * @returns {Promise<boolean>}
   */
  async function updateDagCheck(id, payload) {
    loading.value = true;
    error.value = null;
    
    try {
      await dagRepository.update(id, payload);
      console.log(`Updated DAG check ${id}`);
      
      // Refresh the list
      const check = dagChecks.value.find(c => c.id === id);
      if (check) {
        await fetchDagChecks(check.goatId);
      }
      
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to update DAG check:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a DAG check
   * @param {number} id
   * @returns {Promise<boolean>}
   */
  async function deleteDagCheck(id) {
    loading.value = true;
    error.value = null;
    
    try {
      const check = dagChecks.value.find(c => c.id === id);
      await dagRepository.delete(id);
      console.log(`Deleted DAG check ${id}`);
      
      // Refresh the list if we have the goatId
      if (check) {
        await fetchDagChecks(check.goatId);
      }
      
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to delete DAG check:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get the latest DAG check for a goat
   * @param {number} goatId
   * @returns {Promise<import('../models/dag.model.js').DagCheck|null>}
   */
  async function getLatestCheck(goatId) {
    loading.value = true;
    error.value = null;
    
    try {
      const check = await dagRepository.latest(goatId);
      return check;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch latest DAG check:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    dagChecks,
    loading,
    error,
    
    // Getters
    checkCount,
    criticalChecks,
    concerningChecks,
    healthyChecks,
    latestCheck,
    
    // Actions
    fetchDagChecks,
    fetchAllDagChecks,
    getDagCheckById,
    createDagCheck,
    updateDagCheck,
    deleteDagCheck,
    getLatestCheck
  };
});
